@if (!isDismissed()) {
  <!--
    Alvos de encaixe, só durante o arraste. Ensinam que existem três posições, em vez
    de prometer posicionamento livre e puxar a pill de volta no fim. `pointer-events-none`
    para não roubar o gesto em curso.
  -->
  @if (isMoving()) {
    @for (target of positions; track target) {
      <div
        class="pointer-events-none fixed z-[1201] size-11 rounded-full border-2 border-dashed border-accent-fill-default"
        [class]="layoutFor(target)"
        [class.bg-accent-surface-default]="target === position()"
        data-testid="iara-launcher-drop-target"
        [attr.data-position]="target"
        aria-hidden="true"
      ></div>
    }
  }

  <!--
    `flex-col-reverse` ou `flex-col` vem do mapa de encaixes: ancorado no topo, a pilha
    tem de crescer para BAIXO, senão a pill fica na base dela e desceria ao expandir —
    e geometria que muda no hover é o bug de piscar que já corrigimos uma vez.

    A pill vem primeiro no DOM (é a ação principal e o único tab stop em repouso) e o
    eixo é invertido no CSS quando ela precisa aparecer embaixo, para a ordem de
    tabulação acompanhar a visual.

    Os listeners moram AQUI, não no host: `mouseenter` não borbulha, e o host é um
    elemento sem caixa — seu único filho é `fixed`, fora do fluxo.
  -->
  <div
    class="fixed z-[1202] flex items-end gap-2"
    [class]="launcherClasses()"
    data-testid="iara-launcher"
    [attr.data-position]="position()"
    (mouseenter)="onExpand()"
    (mouseleave)="onCollapse()"
    (focusin)="onExpand()"
    (focusout)="onFocusOut($event)"
  >
    <!--
      Região viva FORA do `@if` dos satélites: uma live region precisa existir no DOM
      antes do texto mudar para ser anunciada. Dentro do `@if` ela nasceria junto com a
      mudança e ficaria muda.
    -->
    <span class="sr-only" role="status" aria-live="polite">{{ moveAnnouncement() }}</span>

    <!--
      Hover muda só brilho, nunca geometria: `scale` num alvo colado na borda faz o
      cursor sair de cima dele e o estado oscilar.
    -->
    <button
      type="button"
      class="flex h-11 items-center whitespace-nowrap rounded-full bg-[image:var(--palantir-gradient-iara-orb)] px-3 text-[length:var(--palantir-font-size-base)] font-semibold text-primary-text-inverse shadow-lg transition-[filter] duration-150 hover:brightness-110 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-fill-default"
      data-testid="iara-fab"
      [attr.aria-expanded]="iaraOpen()"
      [attr.aria-label]="label()"
      (click)="onAskIara()"
    >
      <palantir-iara-glyph [size]="20" />
      <!--
        `aria-label` no botão porque, recolhido, este texto tem largura zero: leitor de
        tela não deve depender de algo que o CSS clipou.

        Anima `max-width`/`margin`/`opacity`, não `width`: a largura de um flex container
        vem do conteúdo e não transiciona. `motion-safe` para quem pediu menos movimento
        ver a troca instantânea, sem perder a informação.
      -->
      <span
        class="overflow-hidden motion-safe:transition-[max-width,margin,opacity] motion-safe:duration-200 motion-safe:ease-out"
        [class]="labelClasses()"
        data-testid="iara-fab-label"
        >{{ label() }}</span
      >
    </button>

    @if (showSatellites()) {
      <!--
        `role="group"` e não `role="menu"`: menu exige navegação por setas entre os
        itens. Aqui as setas agem sobre a alça FOCADA, não como roving tabindex — são
        três botões comuns, cada um um tab stop. Não promova isto a menu.
      -->
      <div
        class="flex items-end gap-1.5"
        [class]="layout().stack"
        role="group"
        aria-label="Outras opções"
      >
        <button
          type="button"
          class="group/action relative flex size-9 items-center justify-center rounded-full bg-neutrals-foreground-default text-neutrals-icon-default shadow-md ring-1 ring-neutrals-border-default transition-colors hover:bg-neutrals-surface-hover hover:text-neutrals-icon-active focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-fill-default motion-safe:animate-[iara-panel-in_160ms_ease-out] motion-safe:[animation-fill-mode:both]"
          data-testid="iara-launcher-support"
          (click)="onTalkToSupport()"
        >
          <span
            class="pointer-events-none absolute right-full mr-2 whitespace-nowrap rounded-md bg-neutrals-surface-inverse px-2 py-1 text-[length:var(--palantir-font-size-xs)] text-neutrals-text-inverse opacity-0 transition-opacity duration-150 group-hover/action:opacity-100 group-focus-visible/action:opacity-100"
          >
            Falar com o suporte
          </span>
          <palantir-icon name="support-agent-outline" />
        </button>

        <!-- `touch-none`: sem isto, arrastar por toque rola a página em vez de mover. -->
        <button
          type="button"
          class="group/action relative flex size-9 touch-none items-center justify-center rounded-full bg-neutrals-foreground-default text-neutrals-icon-default shadow-md ring-1 ring-neutrals-border-default transition-colors hover:bg-neutrals-surface-hover hover:text-neutrals-icon-active focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-fill-default motion-safe:animate-[iara-panel-in_160ms_ease-out] motion-safe:[animation-delay:40ms] motion-safe:[animation-fill-mode:both]"
          [class.ring-accent-border-default]="isMoving()"
          data-testid="iara-launcher-move"
          [attr.aria-describedby]="'iara-launcher-move-hint'"
          (click)="onMoveClick()"
          (keydown)="onMoveKey($event)"
          (keydown.escape)="onMoveCancel()"
          (pointerdown)="onMovePointerDown($event)"
          (pointermove)="onMovePointerMove($event)"
          (pointerup)="onMovePointerUp($event)"
          (pointercancel)="onMoveCancel()"
          (lostpointercapture)="onMoveCancel()"
        >
          <span
            class="pointer-events-none absolute right-full mr-2 whitespace-nowrap rounded-md bg-neutrals-surface-inverse px-2 py-1 text-[length:var(--palantir-font-size-xs)] text-neutrals-text-inverse opacity-0 transition-opacity duration-150 group-hover/action:opacity-100 group-focus-visible/action:opacity-100"
          >
            Mover o botão
          </span>
          <span class="sr-only" id="iara-launcher-move-hint">
            {{ positionDescription() }} Use as setas para cima e para baixo para mover, ou arraste.
          </span>
          <palantir-icon name="drag-dots-outline" />
        </button>

        <!-- Ocultar é a menos importante das três: fica mais discreta até o hover. -->
        <button
          type="button"
          class="group/action relative flex size-9 items-center justify-center rounded-full bg-neutrals-foreground-default text-neutrals-icon-secondary shadow-md ring-1 ring-neutrals-border-default transition-colors hover:bg-neutrals-surface-hover hover:text-neutrals-icon-active focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-fill-default motion-safe:animate-[iara-panel-in_160ms_ease-out] motion-safe:[animation-delay:80ms] motion-safe:[animation-fill-mode:both]"
          data-testid="iara-launcher-dismiss"
          (click)="onDismiss()"
        >
          <span
            class="pointer-events-none absolute right-full mr-2 whitespace-nowrap rounded-md bg-neutrals-surface-inverse px-2 py-1 text-[length:var(--palantir-font-size-xs)] text-neutrals-text-inverse opacity-0 transition-opacity duration-150 group-hover/action:opacity-100 group-focus-visible/action:opacity-100"
          >
            Ocultar até recarregar a página
          </span>
          <palantir-icon name="off-visibility-outline" />
        </button>
      </div>
    }
  </div>
}
